// @flow

import React from "react";

import EditableForm from "../../components/EditableForm";
import FormRow from "../../components/FormRow";
import ConditionalSubform from "../../components/ConditionalSubform";
import EditableField from "../../components/EditableField";
import * as formConditions from "../../utils/formConditions";
import * as mappers from "../../utils/mappers";
import * as v from "../../utils/validators";
import * as formatters from "../../utils/formatters";
import { errors } from "../../utils/errors";
import { placeholders } from "../../utils/placeholders";

import {
  REAL_ESTATE_PROJECT,
  PURPOSE_KIND,
  FEDERAL_STATE,
  REAL_ESTATE_KIND,
  REAL_ESTATE_SCHEDULED_DATE,
  REAL_ESTATE_RESIDUAL_PERCENTAGE,
  REAL_ESTATE_IS_KFW_HELPER,
  REAL_ESTATE_KFW_STANDARD,
  REAL_ESTATE_USAGE_SIZE,
  REAL_ESTATE_BUILDING_SIZE,
  REAL_ESTATE_ZIP_CODE,
  REAL_ESTATE_HERITABLE_BUILDING_RIGHT,
  REAL_ESTATE_HERITABLE_BUILDING_RIGHT_OBLIGATION,
  REAL_ESTATE_USAGE_KIND,
  USE_TYPE__BOTH,
  USE_TYPE__THIRD_PARTY_RENTAL,
  REAL_ESTATE_RENTED_OUT_AREA,
  REAL_ESTATE_COLD_RENT,
  REAL_ESTATE_TAXATION
} from "./formFields";
import { useTypeOptions, nettoBruttoOptions } from "./options";
import {
  mapRealEstateKfwStandardKeys,
  mapPurposeKind,
  getKind,
  mapRealEstateKind,
  mapRealEstateProject
} from "./mappers";
import { type InvestmentSectionProps } from "../../types/details";

const InvestmentRealEstateNew = ({
  initialValues,
  handleSubmit,
  isLocked
}: InvestmentSectionProps) => {
  const shouldShowRentCalculations = formConditions.fieldHasValueIn(REAL_ESTATE_USAGE_KIND, [
    USE_TYPE__BOTH,
    USE_TYPE__THIRD_PARTY_RENTAL
  ]);

  return (
    <EditableForm initialValues={initialValues} onSubmit={handleSubmit} isLocked={isLocked}>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: PURPOSE_KIND,
            caption: () => "Investitionsvorhaben"
          }}
          displayValue={value => {
            const purposeKind = getKind(value, "real_estate");
            return purposeKind && mapPurposeKind(purposeKind.kind);
          }}
        />
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_PROJECT,
            caption: () => "Finanzierungszweck"
          }}
          displayValue={mapRealEstateProject}
        />
        <EditableField
          fieldControlProps={{
            name: FEDERAL_STATE,
            caption: () => "Investitionsort liegt in..."
          }}
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_KIND,
            caption: () => "Investitionsobjekt",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          displayValue={mapRealEstateKind}
        />
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_SCHEDULED_DATE,
            caption: () => "Geplanter Baubeginn",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          inputProps={{ placeholder: placeholders.pleaseSelect }}
          type="monthPicker"
        />
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_RESIDUAL_PERCENTAGE,
            caption: () => "Anteil wohnwirtschaftlicher Nutzung (ca.)"
          }}
          displayValue={formatters.formatPercent}
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_IS_KFW_HELPER,
            caption: () => "Liegt ein KFW-Standard vor?"
          }}
          displayValue={mappers.yesNoMapper}
        />
        <ConditionalSubform
          condition={formConditions.fieldHasValue(REAL_ESTATE_IS_KFW_HELPER, true)}
          renderOnSatisfy={() => (
            <EditableField
              fieldControlProps={{
                name: REAL_ESTATE_KFW_STANDARD,
                caption: () => "Welcher KFW-Standard liegt vor?",
                validate: v.combineWithPrefillCheck(errors.requiredField())
              }}
              displayValue={mapRealEstateKfwStandardKeys}
            />
          )}
          renderOnFail={() => <div />}
        />
        <div />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_USAGE_SIZE,
            caption: () => "Grundstücksgröße",
            validate: v.combineWithPrefillCheck(
              errors.requiredField(),
              v.upperBound(999999999, errors.maxLengthDigits(9))
            )
          }}
          type="areaInput"
          displayValue={formatters.formatArea}
          inputProps={{ placeholder: placeholders.inSquareMeters }}
        />
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_BUILDING_SIZE,
            caption: () => "Gesamtwohn- / Nutzfläche",
            validate: v.combineWithPrefillCheck(
              errors.requiredField(),
              v.upperBound(999999999, errors.maxLengthDigits(9))
            )
          }}
          type="areaInput"
          displayValue={formatters.formatArea}
          inputProps={{ placeholder: placeholders.inSquareMeters }}
        />
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_ZIP_CODE,
            caption: () => "Postleitzahl",
            validate: v.combineWithPrefillCheck(
              errors.requiredField(),
              v.hasLength(5, errors.isZipCode())
            )
          }}
          inputProps={{
            separateThousands: false,
            shouldParseToNumber: false,
            placeholder: "40211",
            leadingZeroes: true
          }}
          type="numberInput"
        />
      </FormRow>
      <FormRow separator>
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_HERITABLE_BUILDING_RIGHT,
            caption: () => "Erbbaurecht?"
          }}
          type="yesNoGroup"
          displayValue={mappers.yesNoMapper}
        />
        <ConditionalSubform
          condition={formConditions.fieldHasValue(REAL_ESTATE_HERITABLE_BUILDING_RIGHT, true)}
          renderOnSatisfy={() => (
            <EditableField
              fieldControlProps={{
                name: REAL_ESTATE_HERITABLE_BUILDING_RIGHT_OBLIGATION,
                caption: () => "Erbbaurechtsverpflichtung",
                validate: v.combineWithPrefillCheck(
                  errors.requiredField(),
                  v.upperBound(999999999, errors.maxLengthDigits(9))
                )
              }}
              type="numberInput"
              displayValue={formatters.formatThousandsCurrency}
              inputProps={{ placeholder: placeholders.inTEuro, sideSymbol: () => "T€" }}
            />
          )}
          renderOnFail={() => {
            return <div />;
          }}
        />
        <div />
      </FormRow>
      <FormRow separator={shouldShowRentCalculations}>
        <EditableField
          fieldControlProps={{
            name: REAL_ESTATE_USAGE_KIND,
            caption: () => "Nutzungsart",
            validate: v.combineWithPrefillCheck(errors.requiredField())
          }}
          type="select"
          displayValue={mappers.optionsMapper}
          inputProps={{ options: useTypeOptions, placeholder: placeholders.pleaseSelect }}
        />
        <div />
        <div />
      </FormRow>
      <ConditionalSubform
        condition={shouldShowRentCalculations}
        renderOnSatisfy={() => (
          <FormRow separator>
            <EditableField
              fieldControlProps={{
                name: REAL_ESTATE_RENTED_OUT_AREA,
                caption: () => "Vermietete Fläche",
                validate: v.combineWithPrefillCheck(
                  errors.requiredField(),
                  v.upperBound(999999999, errors.maxLengthDigits(9))
                )
              }}
              type="areaInput"
              displayValue={formatters.formatArea}
              inputProps={{ placeholder: placeholders.inSquareMeters }}
            />
            <EditableField
              fieldControlProps={{
                name: REAL_ESTATE_COLD_RENT,
                caption: () => "Kaltmiete (pro Jahr)",
                validate: v.combineWithPrefillCheck(
                  errors.requiredField(),
                  v.upperBound(999999999, errors.maxLengthDigits(9))
                )
              }}
              type="numberInput"
              displayValue={formatters.formatCurrency}
              inputProps={{ placeholder: placeholders.inEuro, sideSymbol: () => "€" }}
            />
            <EditableField
              fieldControlProps={{
                name: REAL_ESTATE_TAXATION,
                caption: () => "Netto- / Brutto-Miete"
              }}
              type="radioGroup"
              displayValue={mappers.optionsMapper}
              inputProps={{ options: nettoBruttoOptions }}
            />
          </FormRow>
        )}
        renderOnFail={() => null}
      />
    </EditableForm>
  );
};

export default InvestmentRealEstateNew;
